import { Link, Navigate, getRouteApi } from '@tanstack/react-router';
import { Download, Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type SyntheticEvent } from 'react';

import { api } from '../../api/client.ts';
import type { LoginResponse, SignedInResponse } from '../../api/types.ts';
import { useSession } from '../../auth/session.tsx';
import { Alert, Button, ErrorAlert, Spinner, TextInput } from '../../components/ui.tsx';
import { FormAlerts } from '../../components/FormAlerts.tsx';
import { useFormErrors } from '../../components/useFormErrors.ts';
import { checkCode, checkLength, checkRequired } from '../../forms.ts';
import { useLang } from '../../i18n.ts';
import { useConnection } from '../../layout/connection.ts';
import { serverAway } from '../../layout/connectionState.ts';
import { useDesktopOffline } from '../../platform.ts';
import { AuthLayout } from './AuthLayout.tsx';
import { OfflineSignInForm } from './OfflineSignIn.tsx';

const route = getRouteApi('/login');

type Step =
  | { readonly kind: 'password' }
  | { readonly kind: 'mfa'; readonly mfaToken: string }
  | { readonly kind: 'setup'; readonly mfaToken: string }
  | { readonly kind: 'recovery'; readonly response: SignedInResponse };

/**
 * Sign-in (plan §7.1): password, then the authenticator code for management accounts. A management
 * account without 2FA enrolls it here (QR code) and receives its 10 recovery codes once.
 * The one-time `mfaToken` lives only in this component's memory.
 */
export function LoginPage() {
  const { status, signedIn } = useSession();
  const [step, setStep] = useState<Step>({ kind: 'password' });

  if (status === 'signedIn' && step.kind !== 'recovery') return <Navigate to="/" />;

  const finish = (response: LoginResponse | SignedInResponse) => {
    if (response.status !== 'OK') {
      setStep(
        response.status === 'MFA_REQUIRED'
          ? { kind: 'mfa', mfaToken: response.mfaToken }
          : { kind: 'setup', mfaToken: response.mfaToken },
      );
    } else if ('recoveryCodes' in response) {
      setStep({ kind: 'recovery', response });
    } else {
      signedIn(response);
    }
  };
  const restart = () => {
    setStep({ kind: 'password' });
  };

  switch (step.kind) {
    case 'password':
      return <PasswordStep onDone={finish} />;
    case 'mfa':
      return <MfaStep mfaToken={step.mfaToken} onDone={finish} onRestart={restart} />;
    case 'setup':
      return <SetupStep mfaToken={step.mfaToken} onDone={finish} onRestart={restart} />;
    case 'recovery':
      return (
        <RecoveryCodesStep
          codes={step.response.recoveryCodes ?? []}
          onDone={() => {
            // Leaving the recovery step lets the signed-in redirect below happen.
            setStep({ kind: 'password' });
            signedIn(step.response);
          }}
        />
      );
  }
}

/**
 * Dob Desktop (M7-5): when the server cannot be reached — or the person asks — the PC offers
 * signing in without internet. An offline session already open is announced with a way back to it.
 */
function PasswordStep({ onDone }: { readonly onDone: (r: LoginResponse) => void }) {
  const { t } = useLang();
  const offline = useDesktopOffline();
  const connection = useConnection();
  const [offlineChosen, setOfflineChosen] = useState<boolean | null>(null);
  // Only a real answer: while the first check runs, the screen does not switch (connectionState.ts).
  const away = serverAway(connection.state);
  const showOffline = offline?.available === true && (offlineChosen ?? away);

  if (showOffline) {
    return (
      <AuthLayout title={t('offline.title')} subtitle={t('offline.subtitle')}>
        <div className="flex flex-col gap-4">
          <IdleNotice />
          <OfflineSignInForm
            onCancel={() => {
              setOfflineChosen(false);
            }}
          />
        </div>
      </AuthLayout>
    );
  }
  return (
    <OnlinePasswordStep
      onDone={onDone}
      offlineSessionName={
        offline?.session ? offline.session.nameAr || (offline.session.nameEn ?? '') : null
      }
      onChooseOffline={
        offline?.available
          ? () => {
              setOfflineChosen(true);
            }
          : null
      }
    />
  );
}

/** Dob Desktop ended the last sign-in because the shared PC was left idle (O33 a, D198). */
function IdleNotice() {
  const { t } = useLang();
  return route.useSearch().ended === 'idle' ? (
    <Alert tone="info">{t('auth.idleSignedOut')}</Alert>
  ) : null;
}

function OnlinePasswordStep({
  onDone,
  offlineSessionName,
  onChooseOffline,
}: {
  readonly onDone: (r: LoginResponse) => void;
  readonly offlineSessionName: string | null;
  readonly onChooseOffline: (() => void) | null;
}) {
  const { t } = useLang();
  const { endedUnexpectedly } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // A wrong phone or password stays one message on purpose: it must not tell which one is wrong.
  const errors = useFormErrors(error, {
    fields: { identifier: t('auth.identifier'), password: t('auth.password') },
  });

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (
      !errors.validate({ identifier: checkRequired(identifier), password: checkRequired(password) })
    )
      return;
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<LoginResponse>('/auth/login', { identifier, password }));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('auth.signInTitle')} subtitle={t('auth.signInSubtitle')}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        {offlineSessionName ? (
          <Alert tone="info">
            {t('offline.sessionOpen', { name: offlineSessionName })}{' '}
            <Link to="/offline" className="font-semibold underline">
              {t('offline.continueOffline')}
            </Link>
          </Alert>
        ) : null}
        <IdleNotice />
        {endedUnexpectedly && !error ? <Alert tone="info">{t('auth.sessionEnded')}</Alert> : null}
        <FormAlerts errors={errors} action="sent" />
        <TextInput
          label={t('auth.identifier')}
          hint={t('auth.identifierHint')}
          name="username"
          autoComplete="username"
          inputMode="email"
          dir="ltr"
          required
          value={identifier}
          error={errors.errorFor('identifier')}
          onChange={(e) => {
            errors.touched('identifier');
            setIdentifier(e.target.value);
          }}
          className="text-start"
        />
        <div className="relative">
          <TextInput
            label={t('auth.password')}
            type={visible ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            dir="ltr"
            required
            value={password}
            error={errors.errorFor('password')}
            onChange={(e) => {
              errors.touched('password');
              setPassword(e.target.value);
            }}
            className="pe-11 text-start"
          />
          <button
            type="button"
            onClick={() => {
              setVisible((v) => !v);
            }}
            className="absolute end-1 top-[2.1rem] rounded-md p-2 text-muted hover:text-text"
            aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
            aria-pressed={visible}
          >
            {visible ? (
              <EyeOff className="size-5" aria-hidden />
            ) : (
              <Eye className="size-5" aria-hidden />
            )}
          </button>
        </div>
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
        <Link
          to="/forgot-password"
          className="text-center text-sm font-semibold text-link underline-offset-4 hover:underline"
        >
          {t('auth.forgotPassword')}
        </Link>
        {onChooseOffline ? (
          <button
            type="button"
            onClick={onChooseOffline}
            className="text-center text-sm font-semibold text-link underline-offset-4 hover:underline"
          >
            {t('offline.choose')}
          </button>
        ) : null}
      </form>
    </AuthLayout>
  );
}

function CodeInput({
  value,
  onChange,
  label,
  error,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly error: string | undefined;
}) {
  return (
    <TextInput
      label={label}
      error={error}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      dir="ltr"
      autoFocus
      value={value}
      onChange={(e) => {
        onChange(e.target.value.replace(/\D/g, '').slice(0, 6));
      }}
      className="num h-14 text-center text-2xl tracking-[0.5em]"
    />
  );
}

function MfaStep({
  mfaToken,
  onDone,
  onRestart,
}: {
  readonly mfaToken: string;
  readonly onDone: (r: LoginResponse) => void;
  readonly onRestart: () => void;
}) {
  const { t } = useLang();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const field = recovery ? 'recoveryCode' : 'code';
  const errors = useFormErrors(error, {
    fields: { code: t('auth.code'), recoveryCode: t('auth.recoveryCode') },
    codeFields: { INVALID_CODE: field },
  });

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const ok = errors.validate(
      recovery ? { recoveryCode: checkLength(recoveryCode, 10, 20) } : { code: checkCode(code) },
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      onDone(
        await api.post<LoginResponse>(
          '/auth/mfa/verify',
          recovery ? { mfaToken, recoveryCode: recoveryCode.trim() } : { mfaToken, code },
        ),
      );
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('auth.mfaTitle')} subtitle={recovery ? undefined : t('auth.mfaSubtitle')}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <FormAlerts errors={errors} action="sent" />
        {recovery ? (
          <TextInput
            label={t('auth.recoveryCode')}
            hint={t('auth.recoveryCodeHint')}
            autoComplete="off"
            dir="ltr"
            autoFocus
            value={recoveryCode}
            error={errors.errorFor('recoveryCode')}
            onChange={(e) => {
              errors.touched('recoveryCode');
              setRecoveryCode(e.target.value);
            }}
            className="num text-center text-lg tracking-widest"
          />
        ) : (
          <CodeInput
            label={t('auth.code')}
            value={code}
            error={errors.errorFor('code')}
            onChange={(value) => {
              errors.touched('code');
              setCode(value);
            }}
          />
        )}
        <Button type="submit" variant="primary" size="lg" icon={ShieldCheck} loading={busy}>
          {t('auth.verify')}
        </Button>
        <button
          type="button"
          onClick={() => {
            setRecovery((v) => !v);
            setError(null);
          }}
          className="text-sm font-semibold text-link underline-offset-4 hover:underline"
        >
          {recovery ? t('auth.useAuthenticator') : t('auth.useRecoveryCode')}
        </button>
        <button type="button" onClick={onRestart} className="text-sm text-muted hover:underline">
          {t('auth.backToSignIn')}
        </button>
      </form>
    </AuthLayout>
  );
}

/** Groups the base32 key in fours so it can be typed by hand. */
function groupKey(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Each setup call creates a new secret on the server, so a second call (a remount, React's
 * development double effect) would leave the QR code shown out of step with the saved secret.
 * One request per sign-in attempt (`mfaToken`).
 */
const setupRequests = new Map<string, Promise<{ secret: string; qr: string }>>();

function loadSetup(mfaToken: string): Promise<{ secret: string; qr: string }> {
  let request = setupRequests.get(mfaToken);
  if (!request) {
    request = (async () => {
      const result = await api.post<{ secret: string; otpauthUri: string }>('/auth/mfa/setup', {
        mfaToken,
      });
      // The QR code is drawn in the browser: the secret never goes to a third-party service.
      const { toDataURL } = await import('qrcode');
      return {
        secret: result.secret,
        qr: await toDataURL(result.otpauthUri, { margin: 1, width: 224 }),
      };
    })();
    setupRequests.set(mfaToken, request);
    // A failed request may be tried again (e.g. after the network comes back).
    request.catch(() => setupRequests.delete(mfaToken));
  }
  return request;
}

function SetupStep({
  mfaToken,
  onDone,
  onRestart,
}: {
  readonly mfaToken: string;
  readonly onDone: (r: SignedInResponse) => void;
  readonly onRestart: () => void;
}) {
  const { t } = useLang();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const run = { cancelled: false };
    loadSetup(mfaToken).then(
      (result) => {
        if (!run.cancelled) setSetup(result);
      },
      (e: unknown) => {
        if (!run.cancelled) setLoadError(e);
      },
    );
    return () => {
      run.cancelled = true;
    };
  }, [mfaToken]);

  const errors = useFormErrors(error, {
    fields: { code: t('auth.code') },
    codeFields: { INVALID_CODE: 'code' },
  });

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!errors.validate({ code: checkCode(code) })) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<SignedInResponse>('/auth/mfa/setup/confirm', { mfaToken, code }));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('auth.setupTitle')} subtitle={t('auth.setupIntro')}>
      {loadError ? (
        <div className="flex flex-col gap-4">
          <ErrorAlert error={loadError} />
          <Button onClick={onRestart}>{t('auth.backToSignIn')}</Button>
        </div>
      ) : !setup ? (
        <Spinner />
      ) : (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          <ol className="flex list-decimal flex-col gap-2 ps-5 text-sm">
            <li>{t('auth.setupStep1')}</li>
            <li>{t('auth.setupStep2')}</li>
          </ol>
          <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-white p-4">
            <img src={setup.qr} alt={t('auth.qrAlt')} className="size-56" />
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-link">
              {t('auth.setupManual')}
            </summary>
            <p
              dir="ltr"
              className="num mt-2 select-all break-all rounded-lg bg-surface-2 p-3 text-center font-semibold tracking-wider"
            >
              {groupKey(setup.secret)}
            </p>
          </details>
          <p className="text-sm">{t('auth.setupStep3')}</p>
          <FormAlerts errors={errors} action="sent" />
          <CodeInput
            label={t('auth.code')}
            value={code}
            error={errors.errorFor('code')}
            onChange={(value) => {
              errors.touched('code');
              setCode(value);
            }}
          />
          <Button type="submit" variant="primary" size="lg" icon={ShieldCheck} loading={busy}>
            {t('auth.verify')}
          </Button>
          <button type="button" onClick={onRestart} className="text-sm text-muted hover:underline">
            {t('auth.backToSignIn')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

function RecoveryCodesStep({
  codes,
  onDone,
}: {
  readonly codes: readonly string[];
  readonly onDone: () => void;
}) {
  const { t } = useLang();
  const [saved, setSaved] = useState(false);
  const download = () => {
    const text = `${t('auth.recoveryFileTitle')}\n\n${codes.join('\n')}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dob-burger-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <AuthLayout title={t('auth.recoveryTitle')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm">{t('auth.recoveryIntro')}</p>
        <Alert tone="warning">{t('auth.recoveryWarning')}</Alert>
        <ol
          dir="ltr"
          className="num grid grid-cols-2 gap-2 rounded-xl bg-surface-2 p-4 text-center font-semibold tracking-wider"
        >
          {codes.map((code) => (
            <li key={code} className="select-all rounded-md bg-surface px-2 py-1.5">
              {code}
            </li>
          ))}
        </ol>
        <Button icon={Download} onClick={download}>
          {t('auth.recoveryDownload')}
        </Button>
        <label className="flex items-center gap-3 rounded-lg border border-border p-3 text-sm font-semibold">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => {
              setSaved(e.target.checked);
            }}
            className="size-5 accent-[var(--dob-primary)]"
          />
          {t('auth.recoverySaved')}
        </label>
        <Button variant="primary" size="lg" icon={KeyRound} disabled={!saved} onClick={onDone}>
          {t('common.continue')}
        </Button>
      </div>
    </AuthLayout>
  );
}
